import { toDate } from 'date-fns';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';

import {
  DATE_MONTH_YEAR_LONG_FORMAT,
  DATE_MONTH_YEAR_SHORT_FORMAT,
  DB_DATE_FORMAT,
  DEFAULT_DISPLAY_DATE_FORMAT,
  DEFAULT_PLACEHOLDER,
  DEFAULT_TIME_FORMAT,
  SERVER_FORMAT_DATE,
  SERVER_TIMEZONE,
} from 'core/constants';

export const formatDate = (
  date?: Date | Dayjs | string,
  dateTime = false,
  dateFormat = DEFAULT_DISPLAY_DATE_FORMAT,
  placeholder = DEFAULT_PLACEHOLDER
) => {
  const DATE_TIME_FORMAT = `${dateFormat} ${DEFAULT_TIME_FORMAT}`;

  if (!date) {
    return placeholder;
  }

  const dateValue = isDayjs(date) ? date : dayjs(date);

  if (dateTime) {
    return dateValue.format(DATE_TIME_FORMAT);
  }

  return dateValue.format(dateFormat);
};

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// dayjs words 26 days and more as "a month ago", so the relative wording stops before that.
const RELATIVE_DATE_MAX_DAYS = 26;

// "2 hours ago" / "8 days ago" for recent dates, the regular date format before that. A timestamp
// from the server is read in the server's zone, so the distance to now is right in any zone.
export const formatRelativeDate = (
  date?: Date | Dayjs | string,
  placeholder = DEFAULT_PLACEHOLDER
) => {
  if (!date) {
    return placeholder;
  }

  const dateValue = isDayjs(date) ? date : dayjs.tz(date, SERVER_TIMEZONE);

  if (dayjs().diff(dateValue, 'day') >= RELATIVE_DATE_MAX_DAYS) {
    return formatDate(dateValue);
  }

  return dateValue.fromNow();
};

export const formatDateForServer = (
  date?: Date | Dayjs | string,
  dateFormat = SERVER_FORMAT_DATE,
  placeholder = ''
) => formatDate(date, false, dateFormat, placeholder);

export const getLastDayOfMonth = (date: Date) => {
  const selectedDate = toDate(date);
  const month = selectedDate.getMonth();

  selectedDate.setFullYear(selectedDate.getFullYear(), month + 1, 0);
  selectedDate.setHours(0, 0, 0, 0);

  return selectedDate;
};

export const getDateForInput = (date?: Dayjs | string) => {
  if (!date) {
    return null;
  }

  return dayjs(date);
};

// The user's own calendar day, not UTC (an evening in the Americas is already tomorrow in UTC).
export const getLocalDateForServer = (date: Date = new Date()): string =>
  dayjs(date).format(DB_DATE_FORMAT);

export const subtractDays = (date: Date, days: number): Date =>
  new Date(date.getTime() - days * 24 * 60 * 60 * 1000);

export const getDateOptions = (
  monthsAfter = 12,
  valueFormat = DATE_MONTH_YEAR_SHORT_FORMAT,
  labelFormat = DATE_MONTH_YEAR_LONG_FORMAT
) => {
  const options = [];

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  for (let i = 0; i <= monthsAfter; i++) {
    const date = new Date(currentYear, currentMonth + i, 0);

    options.push({
      value: formatDate(date, false, valueFormat),
      label: formatDate(date, false, labelFormat),
    });
  }

  return options;
};

export const getDateRangeValue = (sinceDate?: string, untilDate?: string) => {
  const sinceDateFormatted = formatDate(sinceDate);
  const untilDateFormatted = formatDate(untilDate);

  if (!sinceDate && !untilDate) {
    return t('labelNoSpecificDates');
  }

  if (!untilDate) {
    return t('labelSinceDate', { date: sinceDateFormatted });
  }

  if (!sinceDate) {
    return t('labelUntilDate', { date: untilDateFormatted });
  }

  return `${sinceDateFormatted} - ${untilDateFormatted}`;
};
